import { PendingSwap, SharedVideoContext } from 'context/useSharedVideoContext';
import { FC, PropsWithChildren, useCallback, useState } from 'react';
import { ChampionshipGame, MmlEvent, MmlEventType, Stream, WscContent } from 'types/generated-types';

export const SharedVideoContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [bracketId, setBracketId] = useState<string>();
  const [broadcaster, setBroadcaster] = useState<string>();
  const [disablePreroll, setDisablePreroll] = useState<boolean>();
  const [event, setEvent] = useState<MmlEvent>();
  const [eventType, setEventType] = useState<MmlEventType | null>();
  const [hasExcitementAlert, setHasExcitementAlert] = useState<boolean>();
  const [openContinuousPlayModal, setOpenContinuousPlayModal] = useState<boolean>();
  const [game, setGame] = useState<ChampionshipGame>();
  const [isMultigame, setIsMultigame] = useState<boolean>();
  const [isPrimary, setIsPrimary] = useState<boolean>();
  const [mediaId, setMediaId] = useState<string | null>();
  const [stream, setStream] = useState<Stream | WscContent>();
  const [title, setTitle] = useState<string>();
  const [useInlineFullscreen, setUseInlineFullscreen] = useState<boolean>();
  const [pendingSwap, setPendingSwap] = useState<PendingSwap>(PendingSwap.NONE);
  const [swapContainerBracketId, setSwapContainerBracketId] = useState<string>();
  const [canPlayVideo, setCanPlayVideo] = useState<boolean>(false);
  const [isMarqueeActive, setIsMarqueeActive] = useState<boolean>(false);
  const [activeTransportContainer, setActiveTransportContainer] = useState<string | null>();

  const resetCanPlayVideo = useCallback(() => {
    setCanPlayVideo(false);
  }, [setCanPlayVideo]);

  const resetVideoParams = useCallback(() => {
    setBracketId('');
    setBroadcaster(undefined);
    setDisablePreroll(false);
    setEvent(undefined);
    setEventType(undefined);
    setHasExcitementAlert(false);
    setOpenContinuousPlayModal(false);
    setGame(undefined);
    setIsMultigame(undefined);
    setIsPrimary(undefined);
    setMediaId('');
    setStream(undefined);
    setTitle('');
    setUseInlineFullscreen(undefined);
    setCanPlayVideo(false);
    resetCanPlayVideo();
    setIsMarqueeActive(false);
  }, [resetCanPlayVideo]);

  const resetContext = useCallback(() => {
    resetVideoParams();
    setTransportContainerReady(undefined);
    setPendingSwap(PendingSwap.NONE);
    setSwapContainerBracketId(undefined);
    setCanPlayVideo(false);
    resetCanPlayVideo();
    setActiveTransportContainer(undefined);
  }, [resetCanPlayVideo, resetVideoParams]);

  const [transportContainerReady, setTransportContainerReady] = useState<string | null>();

  return (
    <SharedVideoContext.Provider
      value={{
        bracketId: bracketId || '',
        broadcaster: broadcaster,
        disablePreroll: !!disablePreroll,
        event: event,
        eventType: eventType,
        hasExcitementAlert: !!hasExcitementAlert,
        openContinuousPlayModal: openContinuousPlayModal,
        game: game,
        isMultigame: isMultigame,
        isPrimary: isPrimary,
        mediaId: mediaId || '',
        stream: stream,
        title: title || '',
        useInlineFullscreen: useInlineFullscreen,
        transportContainerReady: transportContainerReady,
        pendingSwap: pendingSwap,
        swapContainerBracketId: swapContainerBracketId,
        canPlayVideo: canPlayVideo,
        isMarqueeActive: isMarqueeActive,
        activeTransportContainer,
        setBracketId,
        setBroadcaster,
        setDisablePreroll,
        setEvent,
        setEventType,
        setHasExcitementAlert,
        setOpenContinuousPlayModal,
        setGame,
        setIsMultigame,
        setIsPrimary,
        setMediaId,
        setStream,
        setTitle,
        setUseInlineFullscreen,
        setTransportContainerReady,
        setPendingSwap,
        setSwapContainerBracketId,
        setCanPlayVideo,
        resetCanPlayVideo,
        setActiveTransportContainer,
        setIsMarqueeActive,
        resetContext,
        resetVideoParams,
      }}
    >
      {children}
    </SharedVideoContext.Provider>
  );
};
